import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { ECElementEvent } from 'echarts';
import { getPieChartOptionsNew } from '../chartWidgetUtility';
import { ReactComponent as ExpandIcon } from '../../../assets/PepWise2.0/Expand.svg';
import { ReactComponent as CollapseIcon } from '../../../assets/PepWise2.0/Collapse.svg';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import WidgetWraper from 'components/Common/WidgetWraper';
import { InfoTooltip } from '../InfoTooltip';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import CustomSwitch from 'components/Button/CustomSwitch';

interface WidgetProps {
  title?: any;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | string;
  isLoading: boolean;
  isOverlayWidget?: boolean;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  data?: any[];
  envNames?: string[];
  seriesData?: any;
  minTitleHeight?: `${number}px`;
  hasTooltip?: boolean;
  onSliceClick?: (event: ECElementEvent) => void;
  showDollar?: boolean;
  shortDesc?: string | null;
  customSize?: string;
  colorMapping?: any;
  enableSectorCut?: boolean;
  setEnableSectorCut?: any;
  showPercentage?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  hasExpand?: boolean;
  baseHeightRem?: number;
  baseWidthRem?: number;
  expandedScale?: number;
  variant?: 'default' | 'distribution';
  sxOverride?: any;
  wrapperSxOverride?: any;
  labelFontSize?: number;
  isRunDistribution?: boolean;
}

const PieInfoWidget: React.FC<WidgetProps> = ({
  title = '',
  value,
  change,
  changeType,
  isLoading = false,
  tooltipLabel = null,
  seriesData = [],
  hasTooltip = true,
  onSliceClick,
  customSize = '200px',
  showDollar = true,
  colorMapping,
  enableSectorCut,
  setEnableSectorCut,
  showPercentage = false,

  // expand props
  isExpanded = false,
  onToggleExpand,
  hasExpand = true,
  baseHeightRem = 20,
  baseWidthRem = 25,
  expandedScale = 2.1,
  variant = 'default',
  sxOverride = {},
  wrapperSxOverride = {},
  labelFontSize = 14,
  isRunDistribution = false,
}) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<any>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const shouldShowChange = change !== undefined && changeType !== undefined;

  const changeIndicator = useMemo(() => {
    if (!shouldShowChange) {
      return null;
    }

    if (change === 0) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          mt={1}
          color={`${colors.widget.text.noChange} !important`}
        >
          <Typography variant="bodyText" sx={{ ml: 0.5 }}>
            No change vs previous month
          </Typography>
        </Box>
      );
    }

    const isIncrease = changeType === 'increase';

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        mt={1}
        color={
          isIncrease
            ? `${colors.widget.text.changeUp} !important`
            : `${colors.widget.text.changeDown} !important`
        }
      >
        {isIncrease ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        <Typography variant="bodyText" sx={{ ml: 0.5 }}>
          {change}% vs previous month
        </Typography>
      </Box>
    );
  }, [shouldShowChange, change, changeType, colors]);

  const defaultOptions = {
    graphic: [
      {
        type: 'circle',
        left: 'center',
        top: 'center',
        shape: {
          r: 100,
        },
        style: {
          fill: '#e4e4e4',
        },
      },
    ],
    title: {
      show: true,
      textStyle: {
        color: colors.widget.text.main,
        fontWeight: 'normal',
        fontSize: 16,
      },
      text: 'No data',
      left: 'center',
      top: 'center',
    },
    backgroundColor: colors.widget.background.card,
  };

  function getPieCenter(width: number, hasExpand: boolean): [string, string] {
    // Legend is now at the bottom, so the pie is always horizontally centered
    return ['50%', '46%'];
  }

// Distribution variant - responsive center
const getDistributionCenter = (width: number, isExp: boolean): [string, string] => {
  if (!isExp) {
    return ['50%', '50%'];
  }

  // Smooth scaling: 30% → 40% based on width
  const minWidth = 300;
  const maxWidth = 1200;
  const minCenter = 30;
  const maxCenter = 40;

  const scaleFactor = Math.min(Math.max((width - minWidth) / (maxWidth - minWidth), 0), 1);
  const centerX = minCenter + (maxCenter - minCenter) * scaleFactor;

  return [`${Math.round(centerX)}%`, '50%'];
};

// Distribution variant - responsive radius
const getDistributionRadius = (width: number, isExp: boolean): string => {
  if (!isExp) {
    return '55%';
  }

  // Smooth scaling: 55% → 65% as width goes 300px → 1000px
  const minWidth = 300;
  const maxWidth = 1000;
  const minRadius = 55;
  const maxRadius = 65;

  const scaleFactor = Math.min(Math.max((width - minWidth) / (maxWidth - minWidth), 0), 1);
  const radiusValue = minRadius + (maxRadius - minRadius) * scaleFactor;

  return `${Math.round(radiusValue)}%`;
};

// Distribution variant - responsive legend right position
const getResponsiveLegendRight = (width: number): number => {
  // Smooth scaling: 10px → 40px as width goes 300px → 1200px
  const minWidth = 300;
  const maxWidth = 1200;
  const minRight = 10;
  const maxRight = 40;

  const scaleFactor = Math.min(Math.max((width - minWidth) / (maxWidth - minWidth), 0), 1);
  const rightValue = minRight + (maxRight - minRight) * scaleFactor;

  return Math.round(rightValue);
};

// Distribution variant - responsive legend left position
const getResponsiveLegendLeft = (width: number): string => {
 
  const minWidth = 300;
  const maxWidth = 1200;
  const minLeftPercent = 62;
  const maxLeftPercent = 72;

  const scaleFactor = Math.min(Math.max((width - minWidth) / (maxWidth - minWidth), 0), 1);
  const leftPercent = minLeftPercent + (maxLeftPercent - minLeftPercent) * scaleFactor;

  return `${Math.round(leftPercent)}%`;
};

const result = getPieChartOptionsNew({
  theme,
  seriesData,
  containerWidth: chartContainerRef?.current?.offsetWidth,
  colorMapping,
  showDollar: variant?.toLowerCase() === 'distribution' ? false : showDollar,
  showPercentage,
  radius: hasExpand ? '85%' : '65%',
  hasExpand
});
  const adjustedOption: any = useMemo(() => {
    const width = containerWidth || chartContainerRef.current?.offsetWidth || 500;

    const runDistributionNoDataOption = {
    series: [
      {
        type: 'pie',
        radius: getDistributionRadius(width, isExpanded),
        center: getDistributionCenter(width, isExpanded),
        data: [
          {
            value: 1,
            itemStyle: { color: '#e4e4e4' }
          }
        ],
        label: {
          show: true,
          position: 'center',
          formatter: 'No data',
          color: colors.widget.text.main,
          fontSize: 16
        },
        labelLine: { show: false },
        emphasis: { scale: false }
      }
    ]
  };
  
  const hasDistribution = variant?.toLowerCase() === 'distribution';
  const hasNoData = !seriesData?.length;
  
  if (hasDistribution && hasNoData) {
    return runDistributionNoDataOption;
  }
  if (!hasDistribution && (hasNoData || value === 0)) {
    return defaultOptions;
  }
  
  const getCenterValue = () =>
    hasDistribution ? getDistributionCenter(width, isExpanded) : getPieCenter(width, hasExpand);

  const getRadiusValue = (s: any) => {
    if (hasDistribution) return getDistributionRadius(width, isExpanded);
    return s.radius || '50%';
  };

  const getLabelConfig = () => {
    const isSingleSlice = seriesData?.length === 1;
    const isDistributionExpanded = hasDistribution && isExpanded;

    if (!isDistributionExpanded) return { show: false };

    return {
      show: true,
      position: isSingleSlice ? 'center' : 'inside',
      align: isSingleSlice ? 'center' : undefined,
      verticalAlign: isSingleSlice ? 'middle' : undefined,
      formatter: (params: any) => {
        if (isRunDistribution && params.data?.formattedValue) {
          return params.data.formattedValue;
        }
        return params?.value ?? params?.data?.value ?? '';
      },
      fontSize: labelFontSize,
      fontWeight: 600,
      color: '#fff',
    };
  };

    const base = {
      ...result,
      series: result.series.map((s: any) => ({
        type: 'pie',
        data: hasDistribution ? seriesData : s.data,
        label: getLabelConfig(),
        emphasis: {
          scale: true,
          label: { show: false },
        },
        labelLine: { show: false },
        center: getCenterValue(),
        radius: getRadiusValue(s),
      })),
    };

    if (variant?.toLowerCase() === 'distribution' && !isExpanded) {
      (base as any).tooltip = {
        ...result.tooltip,
        formatter: function (params: any) {
          if (!params || !params.name) return '';
          
          const shortName = params.name.length > 10 ? params.name.substring(0, 10) + '...' : params.name;
          const percentage = params.data?.percentage || '';
          const formattedValue = params.data?.formattedValue || '';
          
          return `${shortName}: ${percentage} (${formattedValue})`;
        },
      };
    }

    if (variant?.toLowerCase() === 'distribution' && isExpanded) {
      // Split legends
      const rightLegends = seriesData.slice(0, 20).map((item: any) => item?.name ?? '');
      const bottomLegends = seriesData.slice(20).map((item: any) => item?.name ?? '');
      const hasBottomLegends = bottomLegends.length > 0;

      const baseConfig: any = {
        ...base,
        tooltip: {
          ...result.tooltip,
          renderMode: 'html',
          appendToBody: true,
          extraCssText: 'z-index:10000;',
          formatter: function (params: any): string {
            const color = params.color || params.data?.color || params.data?.itemStyle?.color;
            const shortName = params.name && params.name.length > 10 ? params.name.substring(0, 10) + '...' : params.name;
            return `<div style="display:flex;align-items:center;gap:6px;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color};"></span>
            ${shortName}: ${params.data.percentage} (${params.data.formattedValue})
            </div>`;
          },
        },
      };

      // Add right-side legend for first 20 items
      const legendsConfig: any = {
        right: {
          orient: 'vertical',
          right: getResponsiveLegendRight(width),
          left: getResponsiveLegendLeft(width),
          top: 'middle',
          itemWidth: 16,
          itemHeight: 16,
          data: rightLegends,
          tooltip: {
            show: true,
            enterable: true,
            confine: false,
            appendToBody: true,
            extraCssText: 'z-index:10000;',
            position: function (point: any) {
              return [point[0] + 20, point[1]];
            }
          },
          textStyle: {
            color: colors.widget.chart.labelColorSecondary,
          },
          formatter: function (name: string) {
            const item = seriesData.find((d: any) => d.name === name);
            if (!item) return name;

            // Use displayName if available (RunDistribution), show percentage
            if (isRunDistribution && item.displayName && item.percentage) {
              return `${item.displayName} - ${item.percentage} (${item.formattedValue})`;
            }

            if (item?.percentage) {
              return `${name} - ${item.percentage} (${item.formattedValue})`;
            }
            return name;
          },
        },
      };

      // Add bottom legend for remaining items
      if (hasBottomLegends) {
        legendsConfig.bottom = {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          left: 'center',
          itemWidth: 16,
          itemHeight: 16,
          pageIconColor: '#003771',
          pageIconInactiveColor: '#CCCCCC',
          pageTextStyle: {
            color: '#666666',
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonGap: 8,
          itemGap: 20,
          data: bottomLegends,
          tooltip: {
            show: false,
          },
          textStyle: {
            color: colors.widget.chart.labelColorSecondary,
          },
          formatter: function (name: string) {
            const item = seriesData.find((d: any) => d.name === name);
            if (!item) return name;

            // Return full name with percentage - no truncation, always use item.name for bottom legend
            if (item?.percentage) {
              return `${item.name} - ${item.percentage}`;
            }
            return item.name;
          },
        };
      }

      baseConfig.legend = hasBottomLegends ? [legendsConfig.right, legendsConfig.bottom] : legendsConfig.right;

      return baseConfig;
    }
    return base;
  }, [seriesData, containerWidth, isExpanded, result, hasExpand, variant]);
  // compute doubled sizes when expanded
  const cardHeightRem = useMemo(
    () => (isExpanded ? baseHeightRem * expandedScale : baseHeightRem),
    [isExpanded, baseHeightRem, expandedScale],
  );

  // compute an effective chart min-height so the chart grows with the card
  const effectiveChartMinHeight = useMemo(() => {
    const headerReservePx = 120;
    const cardHeightPx = cardHeightRem * 16;
    const fallbackCustomPx = parseInt(String(customSize), 10) || 0;
    const suggestedPx = Math.max(fallbackCustomPx, Math.max(160, cardHeightPx - headerReservePx));
    return `${suggestedPx}px`;
  }, [customSize, cardHeightRem]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const observer = new ResizeObserver(() => {
      const newWidth = chartContainerRef.current?.offsetWidth || 0;
      setContainerWidth(newWidth);

      setTimeout(() => {
        echartsRef.current?.getEchartsInstance()?.resize();
      }, 50);
    });

    observer.observe(chartContainerRef.current);

    return () => observer.disconnect();
  }, []);

  const formatValue = (value: string | number) => {
      if (value === null || value === undefined || value === '') return value;

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) return value;

      return numericValue.toLocaleString('en-US');
  };

  return (
    <WidgetWraper sx={wrapperSxOverride}>
      <Box
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          display: 'flex',
          flexDirection: 'Column',
          color: `${colors.widget.text.main} !important`,
          height: isExpanded ? `calc(${cardHeightRem}rem + 16px)` : `${cardHeightRem}rem`, // + 16px for padding
          // width: `${cardWidthRem}rem`,
          position: 'relative', // anchor the button
          transition: 'height 300ms ease, width 300ms ease',
          willChange: 'height, width',
          overflow: 'hidden',
          ...sxOverride,
        }}
      >
        {/* bottom-right expand/collapse button */}
        {onToggleExpand && (
          <IconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            size="small"
            onClick={onToggleExpand}
            sx={{
              position: 'absolute', // anchor to corner
              right: variant?.toLowerCase() === 'distribution' ? 12 : 8,
              bottom: variant?.toLowerCase() === 'distribution' ? 12 : 8,
              zIndex: 2,
            }}
          >
            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        )}

        {!isLoading ? (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center">
                <Typography
                  variant="widgetTitle"
                  sx={{ color: `${colors.widget.text.title} !important` }}
                >
                  <HighlightedText text={title} />
                </Typography>
                {hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
              </Stack>
              {!!setEnableSectorCut && (
                <CustomSwitch
                  checked={!!enableSectorCut}
                  onChange={(checked) => setEnableSectorCut(checked)}
                  startLabel="Sector"
                  minHeight={0}
                  containerSx={{ justifyContent: 'flex-end' }}
                  size="small"
                />
              )}
            </Stack>

            {variant?.toLowerCase() !== 'distribution' && (
              <Typography
              variant='widgetValue'
              sx={{
                color: `${colors.widget.text.value} !important`,
              }}
            >
              {formatValue(value)}
            </Typography>
            )}

            {/* Change Indicator */}
            {changeIndicator}

            <Stack flexGrow={1} ref={chartContainerRef} minHeight={effectiveChartMinHeight}>
              <ReactECharts
                ref={echartsRef} // we can call resize()
                style={{ width: '100%', height: '100%' }}
                option={adjustedOption}
                onEvents={{
                  click: (params: ECElementEvent) => {
                    if (onSliceClick && seriesData.length) {
                      onSliceClick(params);
                    }
                  },
                }}
                opts={{ renderer: 'canvas', width: 'auto' }}
                notMerge
                lazyUpdate
              />
            </Stack>
          </>
        ) : (
          <LoaderComponent pt={20} />
        )}
      </Box>
    </WidgetWraper>
  );
};

export default PieInfoWidget;
